import React from 'react';
import styled, { keyframes } from 'styled-components';

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(1, 191, 113, 0.4);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(1, 191, 113, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(1, 191, 113, 0);
  }
`;

const OrderTimeline = ({ status }) => {
  const isCancelled = status === 'Cancelled' || status === 'Canceled';

  if (isCancelled) {
    return (
      <TimelineWrap>
        <StepTrack>
          <StepNode $active $danger>
            <StepIcon>🛒</StepIcon>
            <StepLabel $active $danger>Order Placed</StepLabel>
          </StepNode>
          <Connector $active $danger />
          <StepNode $active $danger $current>
            <StepIcon>✖️</StepIcon>
            <StepLabel $active $danger>Order Cancelled</StepLabel>
          </StepNode>
        </StepTrack>
      </TimelineWrap>
    );
  }

  // Determine active step index
  // 0: Placed, 1: Processing, 2: Out for Delivery / Ready for Pickup, 3: Delivered
  const getActiveIndex = () => {
    switch (status) {
      case 'Delivered':
        return 3;
      case 'Ready for Pickup':
      case 'Out for Delivery':
      case 'Shipped':
        return 2;
      case 'Processing':
        return 1;
      default:
        return 0;
    }
  };

  const activeIdx = getActiveIndex();

  const steps = [
    { label: 'Order Placed', emoji: '🛒' },
    { label: 'Processing', emoji: '⚙️' },
    { label: 'Ready / Delivery', emoji: '🚚' },
    { label: 'Delivered', emoji: '🎉' },
  ];

  return (
    <TimelineWrap>
      <StepTrack>
        {steps.map((step, index) => {
          const isCompleted = index <= activeIdx;
          const isCurrent = index === activeIdx;

          return (
            <React.Fragment key={step.label}>
              <StepNode $active={isCompleted} $current={isCurrent}>
                <StepIcon $active={isCompleted}>{step.emoji}</StepIcon>
                <StepLabel $active={isCompleted} $current={isCurrent}>
                  {step.label}
                </StepLabel>
              </StepNode>
              {index < steps.length - 1 && (
                <Connector $active={index < activeIdx} />
              )}
            </React.Fragment>
          );
        })}
      </StepTrack>
    </TimelineWrap>
  );
};

export default OrderTimeline;

/* ---------- styles ---------- */
const TimelineWrap = styled.div`
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(31, 45, 61, 0.08);
`;

const StepTrack = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding: 4px 0;
`;

const StepNode = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  position: relative;
  z-index: 2;
  flex: 0 0 auto;
`;

const StepIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 0.95rem;
  background: ${props =>
    props.$danger
      ? 'rgba(239, 83, 80, 0.12)'
      : props.$active
      ? 'rgba(1, 191, 113, 0.12)'
      : '#f4f6f8'};
  color: ${props =>
    props.$danger
      ? '#d32f2f'
      : props.$active
      ? '#0a8f57'
      : '#a3b8cc'};
  border: 2px solid
    ${props =>
      props.$danger
        ? '#ef5350'
        : props.$active
        ? '#01bf71'
        : '#dfe3e8'};

  animation: ${props => (props.$current && !props.$danger ? pulse : 'none')} 2s infinite;

  @media screen and (min-width: 480px) {
    width: 38px;
    height: 38px;
    font-size: 1.1rem;
  }
`;

const StepLabel = styled.span`
  font-size: 0.65rem;
  font-weight: ${props => (props.$current ? '800' : '700')};
  color: ${props =>
    props.$danger
      ? '#d32f2f'
      : props.$active
      ? '#1f2d3d'
      : '#8a99a8'};
  text-align: center;
  white-space: normal;
  max-width: 75px;
  line-height: 1.2;

  @media screen and (min-width: 480px) {
    font-size: 0.78rem;
    max-width: 95px;
  }
`;

const Connector = styled.div`
  flex: 1;
  min-width: 10px;
  height: 3px;
  margin: 0 4px;
  margin-bottom: 22px; /* align with icon center */
  background: ${props =>
    props.$danger
      ? '#ef5350'
      : props.$active
      ? '#01bf71'
      : 'rgba(31, 45, 61, 0.1)'};
  border-radius: 999px;
  transition: background 0.3s ease;

  @media screen and (max-width: 480px) {
    margin-bottom: 26px;
    min-width: 6px;
  }
`;
